import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["aria-invalid", "children", "className", "disabled", "fwdRef", "hint", "label", "labelFor", "labelId", "labelWithoutForAttribute", "required"];
import React, { cloneElement, createContext, forwardRef, isValidElement, useContext, useState, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import { FormContext } from '../form/form';
import Col from '../grid/col';
import Row from '../grid/row';
const FormHint = _ref => {
  let {
    hint,
    labelWhenPopupHintPassedToFormRowAsHint
  } = _ref;
  if (!hint || ! /*#__PURE__*/isValidElement(hint)) {
    return null;
  }
  const {
    className: hintPropsClassName
  } = hint.props;
  return /*#__PURE__*/cloneElement(hint, _extends({}, hint.props, {
    className: buildVersionedClassNames('ds-form-row__hint', hintPropsClassName),
    labelWhenPopupHintPassedToFormRowAsHint
  }));
};
const FormRowLabel = _ref2 => {
  let {
    children,
    formRowId,
    group,
    id,
    labelWithoutForAttribute
  } = _ref2;
  return group || labelWithoutForAttribute ? /*#__PURE__*/React.createElement("label", {
    id: id
  }, children) : /*#__PURE__*/React.createElement("label", {
    htmlFor: formRowId,
    id: id
  }, children);
};

/**
 * _Row of a form that includes a label, optional hint and a place for an input._
 *
 * ***
 *
 * `<FormRow>` is always used inside a
 * [`<Form>`](#/Forms/Form) component. It is used for
 * labeling form components and to offer related additional info by
 * setting `hint`. `<Form>` controls the layout of its rows.
 *
 * Use `required` prop to define the input as mandatory. The visual
 * indication is controlled by the `<Form>`. Additional props (including `ref`)
 * will be forwarded to the wrapping `<div>` element.
 */
export const FormRow = _ref3 => {
  let {
      'aria-invalid': ariaInvalid,
      // for FormRow itself
      children,
      className,
      disabled = false,
      fwdRef,
      hint,
      label,
      labelFor,
      labelId: propLabelId,
      labelWithoutForAttribute = false,
      required = false
    } = _ref3,
    props = _objectWithoutPropertiesLoose(_ref3, _excluded);
  const {
    spacing,
    stacked,
    visualizeRequiredFields
  } = useContext(FormContext);
  const [generatedId] = useState(() => "ds-fr-" + uuidv4());
  const [stateLabelId] = useState(() => "ds-fr-label-" + uuidv4());
  const [group, setGroup] = useState(false);
  const [hasGroupItems, setHasGroupItems] = useState(false);
  const [hasRadiogroupRole, setHasRadiogroupRole] = useState(false);
  const [ariaInvalidProp, setAriaInvalidProp] = useState(undefined);
  const id = labelFor != null ? labelFor : generatedId;
  const labelId = propLabelId != null ? propLabelId : stateLabelId;
  const role = (() => {
    if (label && hasGroupItems) {
      return hasRadiogroupRole ? 'radiogroup' : 'group';
    }
    return undefined;
  })();
  const contextValue = useMemo(() => ({
    ariaInvalidProp,
    // for FormRow's children components
    setAriaInvalidProp,
    formRowLabel: label != null ? label : undefined,
    disabled,
    id,
    isInsideFormRow: true,
    labelId: label ? labelId : undefined,
    onGroup: () => setGroup(true),
    onGroupItems: () => setHasGroupItems(true),
    onHasRadiogroupRole: () => setHasRadiogroupRole(true),
    required
  }), [label, disabled, id, labelId, required, ariaInvalidProp]);
  return /*#__PURE__*/React.createElement(Row, _extends({}, props, {
    "aria-labelledby": label && hasGroupItems ? labelId : undefined,
    "aria-required": required && role === 'radiogroup' ? 'true' : undefined,
    className: buildVersionedClassNames('ds-form-row', className, {
      'ds-form-row--disabled': disabled,
      'ds-form-row--required': visualizeRequiredFields && required,
      ["ds-form-row--" + spacing]: spacing !== 'default',
      'ds-form-row--stacked': stacked
    }),
    ref: fwdRef,
    role: role,
    tagName: label && hasGroupItems ? 'fieldset' : 'div',
    "aria-invalid": ariaInvalid != null ? ariaInvalid : ariaInvalidProp
  }), /*#__PURE__*/React.createElement(Col, {
    className: buildVersionedClass('ds-form-row__label'),
    sm: stacked ? undefined : 4
  }, label && /*#__PURE__*/React.createElement(FormRowLabel, {
    formRowId: id,
    group: group,
    id: labelId,
    labelWithoutForAttribute: labelWithoutForAttribute
  }, label), /*#__PURE__*/React.createElement(FormHint, {
    hint: hint,
    labelWhenPopupHintPassedToFormRowAsHint: label != null ? label : undefined
  })), /*#__PURE__*/React.createElement(Col, {
    className: buildVersionedClass('ds-form-row__item'),
    sm: stacked ? undefined : 8
  }, /*#__PURE__*/React.createElement(FormRowContext.Provider, {
    value: contextValue
  }, children)));
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(FormRow, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'FormRow';
export default Fwd;
export const FormRowContext = /*#__PURE__*/createContext({
  ariaInvalidProp: undefined,
  setAriaInvalidProp: () => {},
  disabled: false,
  isInsideFormRow: false,
  onGroup: () => {},
  onGroupItems: () => {},
  onHasRadiogroupRole: () => {},
  required: false
});